import { cmsApi } from '../../services/cmsApi';

export interface ContentThumbnail {
    px_50x50?: string;
    px_600x400?: string;
}

export interface VideoResolution {
    width_in_resolution: number;
    height_in_resolution: number;
}

export interface Content {
    _id: string;
    name: string;
    description?: string;
    url: string;
    signedUrl?: string;
    file_type: string;
    file_size?: number;
    duration?: number;
    orientation?: 'landscape' | 'portrait';
    thumbnail?: ContentThumbnail;
    thumbnailSignedUrls?: ContentThumbnail;
    video_resolution?: VideoResolution;
    tags?: string[];
    createdAt: string;
    updatedAt: string;
}

export interface GetContentsParams {
    search?: string;
    file_type?: string | string[];
    orientation?: string;
    sortBy?: string;
    sortField?: string;
    limit?: number;
    page?: number;
}

export interface GetContentsResponse {
    results: Content[];
    page: number;
    limit: number;
    totalPages: number;
    totalResults: number;
}

export interface SaveContentRequest {
    name: string;
    thumbnail: string;
    data: any;
    player_data?: any;
}

export const contentsApi = cmsApi.injectEndpoints({
    endpoints: (builder) => ({
        getContents: builder.query<GetContentsResponse, GetContentsParams | void>({
            query: (params) => {
                const searchParams = new URLSearchParams();
                if (params) {
                    if (params.page) searchParams.append('page', params.page.toString());
                    if (params.limit) searchParams.append('limit', params.limit.toString());
                    if (params.search) searchParams.append('search', params.search.toString());
                    
                    if (params.file_type) {
                        const types = Array.isArray(params.file_type) ? params.file_type : [params.file_type];
                        types.forEach(t => searchParams.append('file_type', t));
                    }
                }
                return {
                    url: `/externalApp/v1/contents?${searchParams.toString()}`,
                    method: 'GET'
                };
            },
            // Only re-fetch if search, file_type, or orientation change.
            // Page and limit stay within the same cache entry.
            serializeQueryArgs: ({ queryArgs }) => {
                const { page, ...rest } = queryArgs || {};
                return rest;
            },
            // Always merge incoming results with the existing ones for the same cache key
            merge: (currentCache, newItems) => {
                if (newItems.page === 1) {
                    return newItems;
                }
                currentCache.results.push(...newItems.results);
                currentCache.page = newItems.page;
                return currentCache;
            },
            // Refetch when the page changes
            forceRefetch({ currentArg, previousArg }) {
                return currentArg?.page !== previousArg?.page;
            },
            providesTags: (result) =>
                result
                    ? [
                        ...result.results.map(({ _id }) => ({ type: 'Assets' as const, id: _id })),
                        { type: 'Assets', id: 'CONTENTS_LIST' },
                    ]
                    : [{ type: 'Assets', id: 'CONTENTS_LIST' }],
        }),
        getContentById: builder.query<Content, string>({
            query: (id) => ({
                url: `/externalApp/v1/contents/${id}`,
                method: 'GET',
            }),
            providesTags: (_result, _error, id) => [{ type: 'Assets', id }],
        }),
        saveContentToCms: builder.mutation<Content, SaveContentRequest>({
            query: (body) => ({
                url: '/externalApp/v1/contents',
                method: 'POST',
                body,
            }),
            invalidatesTags: [{ type: 'Assets', id: 'CONTENTS_LIST' }],
        }),
        updateContentInCms: builder.mutation<Content, { contentId: string; body: Partial<SaveContentRequest> }>({
            query: ({ contentId, body }) => ({
                url: `/externalApp/v1/contents/${contentId}`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (_result, _error, { contentId }) => [
                { type: 'Assets', id: contentId },
                { type: 'Assets', id: 'CONTENTS_LIST' },
            ],
        }),
    }),
});

export const { useGetContentsQuery, useGetContentByIdQuery, useSaveContentToCmsMutation, useUpdateContentInCmsMutation } = contentsApi;
